"use strict";

/**
 * This module is to support Foobunny localization tooling that handles "delivery zips" (localized
 * text arranged by locale in a directory structure determined by our l10n team) delivered as
 * attachments in Jira localization tickets.
 */

var _require = require('./locale-mapping'),
  siteLocaleToL10nLocale = _require.siteLocaleToL10nLocale;

/*
 * Build a map from l10n locale to its "mapped" locales (not including the l10n locale).
 */
var l10nLocaleLowerToMappedLocalesLower = {};
Object.keys(siteLocaleToL10nLocale).forEach(function (siteLocale) {
  var siteLocaleLower = siteLocale.toLowerCase();
  var l10nLocaleLower = siteLocaleToL10nLocale[siteLocale].toLowerCase();
  // Find the entry for this l10n locale, or create one if it doesn't exist.
  var mappedLocalesLower = l10nLocaleLowerToMappedLocalesLower[l10nLocaleLower];
  if (!mappedLocalesLower) {
    // No entry yet. Initialize a new entry with an empty list.
    mappedLocalesLower = [];
    l10nLocaleLowerToMappedLocalesLower[l10nLocaleLower] = mappedLocalesLower;
  }
  // Don't include the l10n locale in the list of mapped locales.
  if (siteLocaleLower !== l10nLocaleLower) {
    mappedLocalesLower.push(siteLocaleLower);
  }
});

/**
 * This is the immutable data documented at http://www.localeplanet.com/icu/index.html and reflected
 * in the directory names of delivery zips received in Jira localization tickets.
 */
var formalNameToLocaleLower = {
  'Catalan (Spain)': 'ca-es',
  // Not localized yet (Feb 2018)
  'Chinese (China)': 'zh-cn',
  // Not localized yet (Feb 2018)
  'Chinese (Hong Kong)': 'zh-hk',
  // Not localized yet (Feb 2018)
  'Czech (Czech Republic)': 'cs-cz',
  'Danish (Denmark)': 'da-dk',
  'Dutch (Netherlands)': 'nl-nl',
  'English (United Kingdom)': 'en-gb',
  Finnish: 'fi-fi',
  'French (Canada)': 'fr-ca',
  'French (France)': 'fr-fr',
  'German (Germany)': 'de-de',
  'Greek (Greece)': 'el-gr',
  'Indonesian (Indonesia)': 'id-id',
  // Not localized yet (Feb 2018)
  'Italian (Italy)': 'it-it',
  'Japanese (Japan)': 'ja-jp',
  // Not localized yet (Feb 2018)
  'Korean (Korean)': 'ko-kr',
  // Not localized yet (Feb 2018)
  'Norwegian Bokmål (Norway)': 'nb-no',
  // Not localized yet (Feb 2018)
  'Polish (Poland)': 'pl-pl',
  'Portuguese (Brazil)': 'pt-br',
  // Not localized yet (Feb 2018)
  'Portuguese (Portugal)': 'pt-pt',
  'Russian (Russia)': 'ru-ru',
  // Not localized yet (Feb 2018)
  'Spanish (Argentina)': 'es-ar',
  // Not localized yet (Feb 2018)
  'Spanish (Colombia)': 'es-co',
  // Not localized yet (Feb 2018)
  'Spanish (Mexico)': 'es-mx',
  'Spanish (Spain)': 'es-es',
  'Swedish (Sweden)': 'sv-se',
  'Turkish (Turkey)': 'tr-tr' // Not localized yet (Feb 2018)
};

/*
 *Now construct an object like the following.
 *
 *{
 *    'Target-Czech (Czech Republic)': [
 *        l10n: 'cs-cz',
 *        mapped: []
 *    ],
 *    'Target-Danish (Denmark)': [
 *        l10n: 'da-dk',
 *        mapped: []
 *    ],
 *    'Target-Dutch (Netherlands)': [
 *        l10n: 'nl-nl',
 *        mapped: ['nl-be']
 *    ],
 *    'Target-English (United Kingdom)': [
 *        l10n: 'en-gb',
 *        mapped: ['en-au','en-ca','en-hk','en-ie','en-in','en-nz','en-ph','en-sg','en-za','en-zw']
 *    ],
 *    ...
 *}
 *
 *We map "Target" directory names (each associated with an l10n locale) to an object with properties
 *"l10n" (the l10n locale code) and "mapped" (an array of mapped locale codes in no special order).
 *All are in lowercase.
 */
var targetFormalNameToLocales = Object.keys(formalNameToLocaleLower).reduce(function (result, formalName) {
  // Grab the lowercase locale code of the formal name, e.g. 'de-de'.
  var l10nLocaleLower = formalNameToLocaleLower[formalName];
  // Associate the list of site locales that are mapped to it.
  result["Target-".concat(formalName)] = {
    l10n: l10nLocaleLower,
    mapped: l10nLocaleLowerToMappedLocalesLower[l10nLocaleLower]
  };
  return result;
}, {});

/**
 * @param {String} localeFormalName
 *     Indicates an l10n locale for which we're receiving localized properties from l10n team. This
 *     must match the corresponding directory in the "delivery zip", whose name is "Target-" plus
 *     the formal name of the l10n locale as documented at
 *     http://www.localeplanet.com/icu/index.html (e.g. "Target-German (Germany)" for de-DE)
 * @returns {Object}
 *     Object with properties "l10n" (l10n locale code) and "mapped" (array of mapped locale codes
 *     in no special order), or undefined if the given locale formal name is not recognized as one
 *     that we support
 */
function getLocalesForL10nLocaleTargetFormalName(localeFormalName) {
  return targetFormalNameToLocales[localeFormalName];
}

/**
 * Get locales for specific l10n locale folder name.
 * Delivery folder use Locale ISO Code and Country ISO code with an underscore connected, like 'de_DE'.
 * Should be mapped to be Bullet app locale name, like 'de-DE'.
 * Mapping relation examples:
 * ```json
 * {
 *  "cs_CZ": "cs-CZ",
 *  "de_DE": "de-DE",
 *  "en_GB": "en-GB",
 *  "es_MX": "es-MX",
 *  "fr_CA": "fr-CA",
 *  "it_IT": "it-IT",
 *  "pl_PL": "pl-PL",
 *  "sv_SE": "sv-SE",
 *  "da_DK": "da-DK",
 *  "el_GR": "el-GR",
 *  "es_ES": "es-ES",
 *  "fi_FI": "fi-FI",
 *  "fr_FR": "fr-FR",
 *  "nl_NL": "nl-NL",
 *  "pt_PT": "pt-PT"
 * }
 * ```
 *
 * Considering content inheritance (https://wiki.stubcorp.com/pages/viewpage.action?pageId=44341715)
 * the locales LOC team does not support should also be included based on this inheritance rules, and stored in an array named `mapped`.
 *
 * ```javascript
 * getLocalesForL10nLocaleFolderName('de_DE')
 * { locale: 'de-DE', mapped: [ 'de-AT', 'de-CH', 'de-LU' ] }
 * ```
 *
 * @param folderName L10N delivery folder name for each locale translation
 *
 * @typedef {Object} MappingDetails mapping details between l10n folder name and bullet app locales
 * @property {string} locale - mapped locale from folder name
 * @property {[string]} mapped - mapped locales based on content inheritance definition
 * @returns {MappingDetails} mapping details between l10n folder name and bullet app locales
 */
var getLocalesForL10nLocaleISOFolderName = function getLocalesForL10nLocaleISOFolderName(folderName) {
  var locale = folderName.replace('_', '-'); // 'de_DE' => 'de-DE'
  var mapped = Object.keys(siteLocaleToL10nLocale).reduce(function (acc, item) {
    var l10nLocale = siteLocaleToL10nLocale[item];
    if (locale === l10nLocale && locale !== item) {
      acc.push(item);
    }
    return acc;
  }, []);
  return {
    locale: locale,
    mapped: mapped
  };
};
module.exports = {
  getLocalesForL10nLocaleTargetFormalName: getLocalesForL10nLocaleTargetFormalName,
  getLocalesForL10nLocaleISOFolderName: getLocalesForL10nLocaleISOFolderName
};